import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function Chevron() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
      <path fill="currentColor" d="M6 1 10 5H2L6 1Z" />
      <path fill="currentColor" d="M6 11 2 7h8L6 11Z" />
    </svg>
  );
}

export function UiSelect({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Выберите из списка',
  disabled = false,
  className = '',
  style,
  error,
}) {
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState({ left: 0, width: 0, maxH: 300, top: 0, bottom: undefined });

  const selected = useMemo(
    () => options.find((opt) => String(opt.value) === String(value)),
    [options, value],
  );

  const updatePosition = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const pad = 4;
    const below = window.innerHeight - r.bottom - pad;
    const above = r.top - pad;
    const flip = below < 180 && above > below;
    setBox({
      left: r.left,
      width: r.width,
      maxH: Math.min(340, Math.max(120, flip ? above - pad : below - pad)),
      top: flip ? undefined : r.bottom + pad,
      bottom: flip ? window.innerHeight - r.top + pad : undefined,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener('resize', onResize);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (rootRef.current?.contains(t) || listRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const listStyle = {
    position: 'fixed',
    left: box.left,
    width: box.width,
    zIndex: 10050,
    maxHeight: box.maxH,
    overflowY: 'auto',
    ...(box.top != null ? { top: box.top } : { bottom: box.bottom }),
  };

  return (
    <div className={`ui-field ${className}`.trim()} style={style}>
      {label ? <span className="ui-field__label">{label}</span> : null}
      <div className="ui-select" ref={rootRef}>
        <button
          type="button"
          className={`ui-select__trigger${error ? ' is-invalid' : ''}`}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => !disabled && setOpen((v) => !v)}
        >
          <span className={`ui-select__text${selected ? '' : ' ui-select__text--placeholder'}`}>
            {selected ? selected.label : placeholder}
          </span>
          <span className="ui-select__chevron" aria-hidden>
            <Chevron />
          </span>
        </button>
        {open && !disabled && box.width > 0
          ? createPortal(
              <ul className="ui-select__list" ref={listRef} role="listbox" style={listStyle}>
                {options.map((opt) => {
                  const isSelected = String(opt.value) === String(value);
                  return (
                    <li
                      key={String(opt.value)}
                      role="option"
                      aria-selected={isSelected}
                      className={`ui-select__option${isSelected ? ' is-selected' : ''}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        onChange?.(opt.value);
                        setOpen(false);
                      }}
                    >
                      {opt.label}
                    </li>
                  );
                })}
              </ul>,
              document.body,
            )
          : null}
      </div>
      {error ? <span className="form-error">{error}</span> : null}
    </div>
  );
}
